// A page's AI: one thread per page (docs/prime-panel.md). The desktop panel and
// the phone's Agent tab both draw this, so what is said in one is in the other.
// The session is made by the first message and bound to the page, so every
// turn reads the page; writing comes back as page_write cards and pulled-out
// tasks as Task creates (linked to the page on accept). Starters put the usual
// asks one tap away; selected text in the editor rides with the message.
import { trackUse } from '@/state/usage'
import { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { api, engine } from '@/runtime'
import { useNames, usePageThread, useSettings } from '@/db/hooks'
import type { PageResource } from '@/kinds/page/apply'
import type { EditorApi } from '@/kinds/page/editor'
import { DEFAULT_WORKSPACE_SLUG } from '@/kinds/workspace/schema'
import { withSelection } from '@/lib/composer'
import { PAGE_STARTERS, type Starter } from '@/lib/panel'
import { useStream } from '@/state/stream'
import { Turn } from '@/views/Chat'
import { PrimeComposer, type ComposerMessage } from './PrimeComposer'
import { CanvasView } from './CanvasView'
import { Button, cx } from './ui'

export const PageThread = ({
  page,
  editorApi,
  panel = false,
  focusSignal = 0,
  onSent,
}: {
  page: PageResource
  editorApi: EditorApi | null
  /** inside the desktop panel: fills it, and canvases are on the panel's own tab */
  panel?: boolean
  /** bumped by the page's AI entry points (the header's AI, /ai): put the cursor in the box */
  focusSignal?: number
  onSent?: () => void
}) => {
  const session = usePageThread(page.id)
  const sid = session?.id
  const settings = useSettings()
  const { titleOf } = useNames()
  const messages = session?.spec.messages ?? []
  const streamed = useStream((s) => (sid ? (s.text[sid] ?? '') : ''))
  const sentHere = useStream((s) => (sid ? !!s.sentHere[sid] : false))
  const step = useStream((s) => (sid ? (s.step[sid] ?? '') : ''))
  const busy = !!session?.status.busy
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const box = useRef<HTMLDivElement>(null)
  // the thread scrolls in its own box (never the page under it): the panel's body, else the list
  const scroller = useRef<HTMLDivElement & HTMLUListElement>(null)

  useEffect(() => {
    if (sid && session && !session.status.busy) useStream.getState().clear(sid)
  }, [sid, session])
  // follow the thread down
  useEffect(() => {
    const el = scroller.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length, streamed])
  useEffect(() => {
    if (focusSignal) box.current?.querySelector('textarea')?.focus()
  }, [focusSignal])

  const send = async (m: ComposerMessage) => {
    if (sending) return
    const content = withSelection(m.text, editorApi?.selection().trim() ?? '')
    setSending(true)
    setErr(null)
    try {
      // the page's newest words reach the server before the turn reads them
      await engine.sync()
      trackUse('action:prime-message')
      if (sid) {
        useStream.getState().markSent(sid)
        await api.agentSend(sid, content, m.images.length ? { images: m.images } : undefined)
      } else {
        const { session: made } = await api.agentStart({
          workspace: page.labels.ws || DEFAULT_WORKSPACE_SLUG,
          content,
          page_id: page.id,
        })
        useStream.getState().markSent(made.id)
      }
      onSent?.()
      await engine.sync()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'The assistant is not answering')
      throw e
    } finally {
      setSending(false)
    }
  }
  const start = (s: Starter) => {
    if (!s.send) {
      setDraft(s.text)
      requestAnimationFrame(() => {
        const el = box.current?.querySelector('textarea')
        el?.focus()
        el?.setSelectionRange(s.text.length, s.text.length)
      })
      return
    }
    void send({ text: s.text, images: [] }).catch(() => {})
  }
  const waiting = sending || busy

  const list = (
    <ul
      ref={panel ? undefined : scroller}
      className={cx('flex flex-col gap-3 text-sm', !panel && 'max-h-[50dvh] overflow-y-auto pr-1')}
      data-testid="page-thread"
    >
      {messages.length === 0 && (
        <li className="text-xs text-faint">
          Ask about this page, or to change it. Select text in the page first to work on just that.
        </li>
      )}
      {messages.slice(-30).map((m, i) => (
        <Turn key={`${m.at}-${i}`} m={m} titleOf={titleOf} acts={settings.agent_acts && sentHere} />
      ))}
      {/* the panel shows canvases on its own tab; elsewhere they sit in the thread */}
      {!panel &&
        messages
          .slice(-30)
          .filter((m) => m.role === 'assistant' && m.canvas)
          .slice(-1)
          .map((m) => (
            <li key={`canvas-${m.at}`} className="h-72" data-testid="page-thread-canvas">
              <CanvasView canvas={m.canvas!} />
            </li>
          ))}
      {busy && (streamed || step) && (
        <li className="text-sm text-fg-2 whitespace-pre-wrap" data-testid="page-thread-live">
          {streamed || step}
        </li>
      )}
    </ul>
  )

  const composer = (
    <div ref={box}>
      {!draft && !waiting && (
        <div className="flex flex-wrap gap-1.5 pb-2" data-testid="page-starters">
          {PAGE_STARTERS.map((s) => (
            <Button key={s.label} variant="chip" className="shrink-0" onClick={() => start(s)}>
              {s.label}
            </Button>
          ))}
        </div>
      )}
      {err && <p className="mb-1 text-xs text-rose-300">{err}</p>}
      <PrimeComposer
        compact
        enterSends
        busy={waiting}
        pictures={!!sid}
        value={draft}
        onValueChange={setDraft}
        onSend={send}
        onError={setErr}
        placeholder="Ask about this page…"
        label="Message about this page"
        inputTestId="page-thread-input"
        sendTestId="page-thread-send"
      />
    </div>
  )

  if (panel)
    return (
      <>
        <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {list}
        </div>
        <footer className="border-t border-line p-3">{composer}</footer>
      </>
    )
  return (
    <section className="flex flex-col gap-2" data-testid="page-agent">
      {sid && (
        <Link
          to="/agents/$id"
          params={{ id: sid }}
          className="self-end text-xs text-faint hover:text-muted"
          data-testid="page-thread-open"
        >
          Open the whole thread
        </Link>
      )}
      {list}
      {composer}
    </section>
  )
}
